import { useMemo } from 'react';
import { Box, Typography, Stack, Button, Chip } from '@mui/material';
import { saveAs } from 'file-saver';
import Iconify from '../../../../components/iconify';
import { friendlyNameConverter } from '../../../../services/localizationService';
import { descriptiveStats, extractValues } from '../statistics';
import { useTimelineControls } from '../TimelineContext';

const STAT_LABELS = ['Min', 'Max', 'Delta', 'Mean', 'Std Dev', 'Q1', 'Median', 'Q3', 'IQR', 'Mode', 'Count'];
const STAT_KEYS = ['min', 'max', 'delta', 'mean', 'stdev', 'q1', 'median', 'q3', 'iqr', 'mode', 'count'];

/**
 * Recording and sensor names are user-supplied and reach other people through
 * sharing, so both exports have to treat them as data.
 */

/** A value a spreadsheet would evaluate rather than display. */
const FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];

/** One CSV field: doubled quotes, always quoted, formulas defused. */
function csvField(value) {
  const text = value === null || value === undefined ? '' : String(value);
  const safe = FORMULA_PREFIXES.some((p) => text.startsWith(p)) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default function ExportWidget({ recordings = [] }) {
  const { window: timeWindow } = useTimelineControls();
  const isWindowed = timeWindow != null;

  const allStats = useMemo(() => {
    return recordings.map((rec) => {
      const sensorKeys = Object.keys(rec.data || {}).filter(
        (k) => !['altitude', 'latitude', 'longitude'].some((g) => k.toLowerCase().endsWith(g))
      );
      const sensors = sensorKeys.map((key) => ({
        key,
        name: friendlyNameConverter(key),
        stats: descriptiveStats(extractValues(rec, key, timeWindow?.start, timeWindow?.end)),
      }));
      return { recording: rec, sensors };
    });
  }, [recordings, timeWindow]);

  const totalSensors = allStats.reduce((sum, r) => sum + r.sensors.length, 0);

  const exportCsv = () => {
    const rows = [];
    const header = ['Recording', 'Sensor', ...STAT_LABELS];
    rows.push(header.join(','));

    for (const { recording, sensors } of allStats) {
      for (const { name, stats } of sensors) {
        if (!stats) continue;
        const values = STAT_KEYS.map((k) => csvField(stats[k]));
        rows.push([csvField(recording.name), csvField(name), ...values].join(','));
      }
      rows.push(''); // blank line between recordings
    }

    const windowLabel = isWindowed ? '_windowed' : '_full';
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, `workspace_stats${windowLabel}.csv`);
  };

  const openPrintView = () => {
    const win = window.open('', '_blank');
    if (!win) return;

    let html = `<html><head><title>Workspace Statistics Report</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; }
        h2 { margin: 20px 0 5px; }
        h3 { margin: 10px 0 5px; color: #555; }
        table { border-collapse: collapse; width: 100%; margin-bottom: 15px; }
        th, td { border: 1px solid #ddd; padding: 4px 8px; text-align: right; }
        th { background: #f5f5f5; text-align: left; }
        td:first-child { text-align: left; }
        .windowed { color: #0288d1; font-style: italic; }
      </style></head><body>`;

    html += `<h2>Workspace Statistics Report</h2>`;
    if (isWindowed) {
      html += `<p class="windowed">Time window applied</p>`;
    }

    for (const { recording, sensors } of allStats) {
      html += `<h3>${escapeHtml(recording.name)}</h3>`;
      html += `<table><tr><th>Sensor</th>${STAT_LABELS.map((l) => `<th>${escapeHtml(l)}</th>`).join('')}</tr>`;
      for (const { name, stats } of sensors) {
        if (!stats) continue;
        const cells = STAT_KEYS.map((k) => {
          const v = stats[k];
          if (v === null || v === undefined) return '<td>—</td>';
          return `<td>${escapeHtml(typeof v === 'number' ? v.toFixed(2) : v)}</td>`;
        }).join('');
        html += `<tr><td>${escapeHtml(name)}</td>${cells}</tr>`;
      }
      html += `</table>`;
    }

    html += `</body></html>`;
    win.document.write(html);
    win.document.close();
    win.print();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Iconify icon="eva:file-text-fill" width={36} sx={{ color: 'text.secondary', mb: 1 }} />
      <Typography variant="subtitle2" gutterBottom>Export Statistics</Typography>
      <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
        <Chip label={`${recordings.length} recording${recordings.length !== 1 ? 's' : ''}`} size="small" variant="outlined" />
        <Chip label={`${totalSensors} sensors`} size="small" variant="outlined" />
        {isWindowed && <Chip label="Windowed" size="small" color="info" variant="outlined" />}
      </Stack>
      <Stack spacing={1} sx={{ width: '100%' }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<Iconify icon="eva:download-fill" width={18} />}
          onClick={exportCsv}
          fullWidth
        >
          Export CSV
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Iconify icon="eva:printer-fill" width={18} />}
          onClick={openPrintView}
          fullWidth
        >
          Print Report
        </Button>
      </Stack>
    </Box>
  );
}
